import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Check, ArrowLeft, MessageCircle, Info, CheckCircle2, Ban, ExternalLink, Printer,
} from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { buildWhatsappLinks, bookingWhatsappMessage } from "@/lib/whatsappLink";

const CUR = "\u20b9";
const fmt = (n) =>
  n === null || n === undefined
    ? "—"
    : `${CUR}${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function QuoteReview() {
  const { settings } = useApp();
  const { id } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [withoutAdvance, setWithoutAdvance] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/quotes/${id}`);
      setQ(data);
    } catch (e) {
      toast.error(errMsg(e, "Could not load quote"));
      navigate("/account");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const approve = async () => {
    if (!acceptTerms) {
      toast.error("Please accept the Terms & Conditions to continue");
      return;
    }
    if ((confirm || "").trim().toLowerCase() !== (q?.customer_name || "").trim().toLowerCase()) {
      toast.error("Please type your name exactly as shown to confirm");
      return;
    }
    setActing(true);
    try {
      const { data } = await api.post(`/quotes/${id}/approve`, {
        accept_terms: true,
        without_advance: withoutAdvance,
      });
      toast.success(`Booking confirmed · ${data.booking?.reference}`);
      navigate(`/account/bookings/${data.booking.id}`);
    } catch (e) {
      toast.error(errMsg(e, "Approval failed"));
    } finally {
      setActing(false);
    }
  };

  const decline = async () => {
    if (!window.confirm("Decline this quote? This cannot be undone.")) return;
    setActing(true);
    try {
      await api.post(`/quotes/${id}/decline`);
      toast.success("Quote declined");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Decline failed"));
    } finally {
      setActing(false);
    }
  };

  const advanceWhatsappLinks = useMemo(() => {
    if (!q) return [];
    return buildWhatsappLinks(
      settings,
      bookingWhatsappMessage({
        booking: {
          reference: `(pending quote ${q.id?.slice(0, 8)})`,
          event_type: q.event_type,
          event_date: q.event_date,
          guest_count: q.guest_count,
          package_snapshot: q.package_snapshot,
          final_amount: q.final_amount,
          advance_requested: q.advance_requested,
        },
        customerName: q.customer_name,
        purpose: "advance",
      })
    );
  }, [q, settings]);

  if (loading || !q) {
    return <div className="container-x py-16 text-sm text-muted-foreground">Loading…</div>;
  }

  const isPrepared = q.status === "prepared";
  const isTerminal = ["approved", "declined", "expired"].includes(q.status);
  const hasAdvance = q.advance_requested != null && Number(q.advance_requested) > 0;
  const nameMatches =
    (confirm || "").trim().toLowerCase() === (q.customer_name || "").trim().toLowerCase();
  const canApprove = acceptTerms && nameMatches && !acting;

  return (
    <div className="min-h-screen bg-muted/40">
      <div className="container-x py-8">
        <Link to="/account" data-testid="quote-back" className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back to Dashboard
        </Link>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <span className="kbd-eyebrow">Quote</span>
            <h1 className="mt-2 font-serif-display text-3xl font-semibold text-secondary">Review your quote</h1>
          </div>
          <StatusChip status={q.status} />
        </div>
        <div className="mt-3">
          <Link
            to={`/account/quotes/${id}/print`}
            data-testid="quote-print-link"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-background px-3 py-1.5 text-xs font-semibold text-secondary hover:bg-muted"
          >
            <Printer className="h-3.5 w-3.5" /> Printable quote
          </Link>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_360px]">
          <div className="space-y-6">
            <section className="surface-card" data-testid="quote-event-section">
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">Event</div>
              <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
                <Field label="Event type" value={q.event_type} />
                <Field label="Guests" value={q.guest_count} testid="quote-guests" />
                <Field label="Event date" value={q.event_date} />
                <Field label="Event time" value={q.event_time} />
                <Field label="Venue" value={q.venue} full />
                <Field label="Special requirements" value={q.special_requirements} full />
              </dl>
            </section>

            {q.package_snapshot && (
              <section className="surface-card" data-testid="quote-package-section">
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">Package</div>
                <div className="mt-2 font-serif-display text-xl font-semibold text-secondary">
                  {q.package_snapshot.name}
                </div>
                {q.package_snapshot.description && (
                  <p className="mt-1 text-sm text-muted-foreground">{q.package_snapshot.description}</p>
                )}
                <div className="mt-3 text-sm text-secondary/80">
                  {fmt(q.package_snapshot.price)}{q.package_snapshot.pricing_unit ? ` / ${q.package_snapshot.pricing_unit}` : ""}
                  {" · "}min {q.package_snapshot.minimum_guests || "—"} guests
                </div>
              </section>
            )}

            {q.dishes_snapshot?.length > 0 && (
              <section className="surface-card" data-testid="quote-dishes-section">
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">
                  Selected dishes ({q.dishes_snapshot.length})
                </div>
                <ul className="mt-3 grid gap-1 text-sm sm:grid-cols-2">
                  {q.dishes_snapshot.map((d) => (
                    <li key={d.id} className="rounded-lg border border-border/70 bg-background px-3 py-1.5">
                      <span className="font-medium text-secondary">{d.name}</span>
                      <span className="ml-1 text-[10px] uppercase tracking-[0.14em] text-accent">
                        {d.category_name}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {q.extras_snapshot?.length > 0 && (
              <section className="surface-card" data-testid="quote-extras-section">
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">Extras</div>
                <ul className="mt-3 space-y-1 text-sm">
                  {q.extras_snapshot.map((e, i) => (
                    <li key={i} className="flex items-center justify-between rounded-lg border border-border/70 bg-background px-3 py-1.5">
                      <span><span className="font-medium text-secondary">{e.category_name}</span> · ×{e.qty}</span>
                      <span className="text-xs text-muted-foreground">{e.rate_per_head !== null && e.rate_per_head !== undefined ? `${fmt(e.rate_per_head)} / head` : "TO BE CONFIGURED"}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {settings?.terms_and_conditions && (
              <section className="surface-card" data-testid="quote-terms-section">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">Terms &amp; Conditions</div>
                  <Link
                    to="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="quote-terms-fullscreen-link"
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline"
                  >
                    Open full page <ExternalLink className="h-3 w-3" />
                  </Link>
                </div>
                <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap rounded-xl bg-muted/40 p-4 text-[11px] text-secondary/80">
                  {settings.terms_and_conditions}
                </pre>
              </section>
            )}
          </div>

          <aside className="surface-card h-fit lg:sticky lg:top-24" data-testid="quote-price-summary">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">Price summary</div>
            {q.pricing_snapshot?.package_amount != null && (
              <Row label="Package" value={fmt(q.pricing_snapshot.package_amount)} testid="qs-package" />
            )}
            {q.pricing_snapshot?.extras_amount != null && q.pricing_snapshot.extras_amount > 0 && (
              <Row label="Extras" value={fmt(q.pricing_snapshot.extras_amount)} testid="qs-extras" />
            )}
            <Row label="Subtotal" value={
              q.pricing_snapshot?.subtotal !== null && q.pricing_snapshot?.subtotal !== undefined
                ? fmt(q.pricing_snapshot?.subtotal)
                : `Partial · ${fmt(q.pricing_snapshot?.subtotal_partial)}`
            } testid="qs-subtotal" />
            {q.additional_charges?.length > 0 && (
              <div className="mt-3 border-t border-border/60 pt-3">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Additional charges</div>
                <ul className="mt-1 space-y-1 text-xs">
                  {q.additional_charges.map((c, i) => (
                    <li key={i} data-testid={`qs-charge-${i}`} className="flex items-center justify-between">
                      <span className="text-secondary/80">{c.label}</span>
                      <span className="font-semibold text-secondary">{fmt(c.amount)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {q.gst_snapshot?.enabled && (
              <div className="mt-3 space-y-1 border-t border-border/60 pt-3 text-xs">
                <div className="flex items-baseline justify-between">
                  <span className="text-muted-foreground">Taxable subtotal</span>
                  <span className="font-semibold text-secondary" data-testid="qs-gst-taxable">{fmt(q.gst_snapshot.taxable_subtotal)}</span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-muted-foreground">GST ({q.gst_snapshot.rate}%)</span>
                  <span className="font-semibold text-secondary" data-testid="qs-gst-amount">{fmt(q.gst_snapshot.gst_amount)}</span>
                </div>
              </div>
            )}
            <div className="mt-4 flex items-baseline justify-between border-t border-border/70 pt-3">
              <div className="text-sm font-semibold uppercase tracking-[0.16em] text-secondary">Final amount</div>
              <div className="text-2xl font-bold text-secondary" data-testid="qs-final-amount">{fmt(q.final_amount)}</div>
            </div>
            {hasAdvance && (
              <div className="mt-2 flex items-baseline justify-between text-xs">
                <span className="text-muted-foreground">Advance requested</span>
                <span className="font-semibold text-secondary" data-testid="qs-advance">{fmt(q.advance_requested)}</span>
              </div>
            )}

            {isPrepared && (
              <div className="mt-6 space-y-4" data-testid="quote-approve-block">
                {/* Inline T&Cs acceptance */}
                <label className="flex items-start gap-2 rounded-xl border border-border/70 bg-background p-3 text-xs text-secondary">
                  <input
                    type="checkbox"
                    data-testid="quote-accept-terms"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-border text-primary focus:ring-primary"
                  />
                  <span>
                    I have read and accept the{" "}
                    <Link
                      to="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="quote-terms-inline-link"
                      className="font-semibold text-primary hover:underline"
                    >
                      Terms &amp; Conditions
                    </Link>
                    . I understand pricing is server-authoritative and this booking follows the owner-approved T&amp;Cs.
                  </span>
                </label>

                {/* Confirm-without-advance choice — only when admin requested one */}
                {hasAdvance && (
                  <div className="rounded-xl border border-border/70 bg-background p-3" data-testid="quote-advance-choice">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                      Advance {fmt(q.advance_requested)}
                    </div>
                    <div className="mt-2 space-y-2 text-xs text-secondary">
                      <label className="flex items-start gap-2">
                        <input
                          type="radio"
                          name="advance-choice"
                          data-testid="quote-advance-arrange"
                          checked={!withoutAdvance}
                          onChange={() => setWithoutAdvance(false)}
                          className="mt-0.5"
                        />
                        <span>
                          <span className="font-semibold text-secondary">Arrange advance on WhatsApp</span>
                          <span className="mt-0.5 block text-[11px] text-muted-foreground">
                            Booking is created as <span className="font-mono">payment_pending</span>. Our team will contact you to confirm the advance offline. No online payment is charged here.
                          </span>
                        </span>
                      </label>
                      <label className="flex items-start gap-2">
                        <input
                          type="radio"
                          name="advance-choice"
                          data-testid="quote-without-advance"
                          checked={withoutAdvance}
                          onChange={() => setWithoutAdvance(true)}
                          className="mt-0.5"
                        />
                        <span>
                          <span className="font-semibold text-secondary">Confirm without advance</span>
                          <span className="mt-0.5 block text-[11px] text-muted-foreground">
                            Skip the advance step now. Booking is created as <span className="font-mono">confirmed</span>; our team will settle the advance offline as per T&amp;Cs.
                          </span>
                        </span>
                      </label>
                    </div>

                    {!withoutAdvance && advanceWhatsappLinks.length > 0 && (
                      <div className="mt-3 flex flex-col gap-2">
                        {advanceWhatsappLinks.map((l, i) => (
                          <a
                            key={l.number + i}
                            data-testid={`quote-wa-advance-${i + 1}`}
                            href={l.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-[11px] font-semibold text-white"
                          >
                            <MessageCircle className="h-3.5 w-3.5" />
                            Message advance team {advanceWhatsappLinks.length > 1 ? i + 1 : ""}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <label className="block text-xs">
                  <span className="mb-1 block font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    Type "<span className="text-secondary">{q.customer_name}</span>" to confirm
                  </span>
                  <input
                    data-testid="quote-approve-confirm"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="w-full rounded-xl border border-border/70 bg-background px-3 py-2 text-sm"
                    placeholder={q.customer_name || ""}
                  />
                </label>
                <button
                  data-testid="quote-approve"
                  onClick={approve}
                  disabled={!canApprove}
                  className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  {withoutAdvance && hasAdvance ? "Confirm without advance" : "Approve & Book"}
                </button>
                <button
                  data-testid="quote-decline"
                  onClick={decline}
                  disabled={acting}
                  className="btn-ghost w-full !text-destructive !border-destructive/60"
                >
                  <Ban className="h-4 w-4" /> Decline
                </button>
              </div>
            )}
            {!isPrepared && !isTerminal && (
              <div className="mt-4 flex items-start gap-2 rounded-xl border border-accent/40 bg-accent/10 p-3 text-xs text-secondary">
                <Info className="h-3.5 w-3.5 shrink-0" />
                Our team is finalising your quote. You will be notified when it is ready to approve.
              </div>
            )}
            {q.status === "approved" && q.booking_id && (
              <Link
                to={`/account/bookings/${q.booking_id}`}
                data-testid="quote-view-booking"
                className="btn-primary mt-5 w-full"
              >
                <Check className="h-4 w-4" /> View Booking
              </Link>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    submitted:  { label: "Submitted", cls: "bg-muted text-secondary" },
    prepared:   { label: "Ready for your approval", cls: "bg-accent/20 text-secondary" },
    approved:   { label: "Approved", cls: "bg-primary/10 text-primary" },
    declined:   { label: "Declined", cls: "bg-destructive/10 text-destructive" },
    expired:    { label: "Expired", cls: "bg-muted text-muted-foreground" },
    new:        { label: "New", cls: "bg-muted text-secondary" },
    reviewed:   { label: "Reviewed", cls: "bg-muted text-secondary" },
  };
  const s = map[status] || { label: status, cls: "bg-muted text-secondary" };
  return (
    <span data-testid="quote-status" className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] ${s.cls}`}>
      {s.label}
    </span>
  );
}

function Field({ label, value, full, testid }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-medium text-secondary" data-testid={testid}>{value || "—"}</div>
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="mt-2 flex items-baseline justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold text-secondary" data-testid={testid}>{value}</span>
    </div>
  );
}
